import React, { Fragment } from "react";
import { Vector2 } from "three";
import { CoordinateSystem } from "./CoordinateSystem";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import { SVG } from "./SVG";
import { kantenZuStrecken, kanteZuStrecke, Strecke2 } from "../../arrangement/grundrissFunctions";
import { Segment } from "../../arrangement/Segment";
import { createWinkel, Winkel } from "./Winkel";
import { getKanteInfo, Zaunlinie } from "./Zaunlinie";
import { Part } from "./Part";
import { WarnungSvg } from "./WarnungSvg";
import { Stromkabellinie } from "./Stromkabellinie";
import { Arrangement } from "../../arrangement/Arrangement";
import { Grundriss } from "../../arrangement/Grundriss";

export const laengeDisplayPxMinimum = 50;

export function Zaunlinien({
  cs,
  editedKanteId,
  setEditedKanteId,
  forSnapshot = false,
}: {
  cs: CoordinateSystem;
  editedKanteId: number;
  setEditedKanteId: (v: number) => void;
  forSnapshot?: boolean;
}) {
  const agt = APP.arrangement;
  const grundriss = agt.grundriss;
  const winkel = createWinkel(cs, kantenZuStrecken(grundriss.kanten));
  const parts = APP.arrangement
    .getPartsAndPfosten()
    .filter((part) => part.isPfosten || part.isElement);
  const rulers = maybeFilterRulerFürGeteilteGrundrisskanten(agt, getRulers(agt, grundriss));

  return (
    <>
      <SVG cs={cs} className={"z-index-0"}>
        {grundriss.kanten.map((kante) => (
          <Zaunlinie
            key={kante.id}
            cs={cs}
            strecke={kanteZuStrecke(kante)}
            kanteId={kante.id}
            editable={true}
            editedKanteId={editedKanteId}
            setEditedKanteId={setEditedKanteId}
            mode={"SVG-zindex-0"}
            style={"GrundrissKante"}
            forSnapshot={forSnapshot}
          />
        ))}
        {winkel.map((winkel, index) => (
          <Winkel key={index} cs={cs} winkel={winkel} />
        ))}
      </SVG>
      <SVG cs={cs} className={"z-index-1"}>
        {agt.kabel.map((kabel, index) => (
          <Stromkabellinie key={index} cs={cs} kabel={kabel} />
        ))}
        {grundriss.kanten.map((kante) => (
          <Zaunlinie
            key={kante.id}
            cs={cs}
            strecke={kanteZuStrecke(kante)}
            editable={true}
            kanteId={kante.id}
            editedKanteId={editedKanteId}
            setEditedKanteId={setEditedKanteId}
            mode={"SVG-zindex-1"}
            style={"GrundrissKante"}
            forSnapshot={forSnapshot}
          />
        ))}
      </SVG>

      <SVG cs={cs} className={"z-index-2"}>
        {parts.map((part, index) => (
          <Part key={index} cs={cs} part={part} />
        ))}
        {APP.arrangement.warnungen.map((warnung, index) => (
          <WarnungSvg key={index} cs={cs} warnung={warnung} mode={"SVG-zindex-2"} />
        ))}
      </SVG>
      {grundriss.kanten.map((kante) => (
        <Zaunlinie
          key={kante.id}
          cs={cs}
          editable={true}
          strecke={kanteZuStrecke(kante)}
          kanteId={kante.id}
          editedKanteId={editedKanteId}
          setEditedKanteId={setEditedKanteId}
          mode={"HTML"}
          style={"GrundrissKante"}
          forSnapshot={forSnapshot}
        />
      ))}

      {(
        [
          { style: "Tor", offset: 20 },
          { style: "Schiebetorüberstand", offset: 20 },
          { style: "GanzesSegment", offset: 80 },
          {
            style: "Segment",
            offset: 110,
          },
        ] as { style: "Tor" | "GanzesSegment" | "Segment" | "GrundrissKante" | "Schiebetorüberstand"; offset: number }[]
      ).map((r, index) => (
        <Fragment key={index}>
          <SVG cs={cs} className={"z-index-0"}>
            {rulers
              .filter(({ style }) => style == r.style)
              .map(({ punkte }, index) => (
                <Zaunlinie
                  key={index}
                  cs={cs}
                  strecke={punkte}
                  mode={"SVG-zindex-0"}
                  offset={r.offset}
                  editable={false}
                  style={r.style}
                  forSnapshot={forSnapshot}
                />
              ))}
          </SVG>
          {rulers
            .filter(({ style }) => style == r.style)
            .map(({ punkte }, index) => (
              <Zaunlinie
                key={index}
                cs={cs}
                strecke={punkte}
                mode={"HTML"}
                offset={r.offset}
                editable={false}
                style={r.style}
                forSnapshot={forSnapshot}
              />
            ))}
        </Fragment>
      ))}

      {APP.arrangement.warnungen.map((warnung, index) => (
        <WarnungSvg key={index} cs={cs} warnung={warnung} mode={"HTML"} />
      ))}
    </>
  );
}

export function punkteZuLinie(punkte: Vector2[]) {
  return {
    x1: punkte[0].x,
    y1: punkte[0].y,
    x2: punkte[1].x,
    y2: punkte[1].y,
  };
}

type TStyle = "Segment" | "Leerraum" | "GanzesSegment" | "Element" | "Tor" | "Überstand" | "Schiebetorüberstand" | "";

interface TRuler {
  segment: Segment;
  punkte: Strecke2;
  x0: number;
  x1: number;
  style: TStyle;
}

function getRulers(agt: Arrangement, grundriss: Grundriss): TRuler[] {
  return agt.segmente.flatMap((segment) =>
    segment.ruler
      .map(({ x0, x1, style }) => ({
        segment,
        style,
        x0,
        x1,
        punkte: [x0, x1].map((x) => Maths3.v3To2xz(segment.getWorldPositionOfX(x))) as Strecke2,
      }))
      .filter((ruler) => {
        const kante = segment.segmentKante.grundrissKante;
        if (kante == null || ruler.style == "") {
          return false;
        }
        const { laenge } = getKanteInfo(kante);
        return Math.abs(Maths2.distance(ruler.punkte) - laenge) > 0.01;
      })
  );
}

// GanzesSegment-Ruler nur anzeigen, wenn Grundrisskante durch Aufbau unterteilt wird
function maybeFilterRulerFürGeteilteGrundrisskanten(agt: Arrangement, ruler: TRuler[]) {
  return ruler.filter(
    ({ segment, style }) =>
      !(
        style == "GanzesSegment" &&
        agt.segmente.filter(
          (s) => s.segmentKante.grundrissKante === segment.segmentKante.grundrissKante
        ).length < 2
      )
  );
}
